/**
 * Partner / Channel-Partner Onboarding Wizard — Admin Portal
 * ==========================================================
 * Banking-grade, audit-first KYB onboarding wired to the disbursement client
 * onboarding lifecycle:
 *
 *   create → submit (Sections A–E) → KYB document upload (+ AI extraction) →
 *   sanctions screening → AI verification + report → maker review →
 *   checker approval (4-eyes) → activation (float 2100-20-NNN + deposit ref +
 *   rep self-registration link).
 *
 * The immutable kyb_audit_log is rendered alongside every step.
 *
 * @see services/disbursement/clientOnboardingService.js
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  fieldInputClass,
  validatePartnerOnboardingCreateForm,
  validateEmail,
} from '../../utils/contactFieldValidation';

const API = axios.create({ baseURL: '/api/v1' });

// Designated four-eyes checker for production CP onboarding (maker ≠ checker).
const PRODUCTION_CHECKER_EMAIL = 'andrejr@mymoolah.africa';
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// The rep self-registration link lives on the wallet app, not the portal.
const WALLET_BASE_URL =
  (import.meta.env.VITE_WALLET_BASE_URL as string) || 'https://wallet.mymoolah.africa';

/** Fixed Client Portal login — same for every partner (onboarding handoff after Step 1). */
function clientPortalLoginUrl(): string {
  const env = import.meta.env.VITE_CLIENT_PORTAL_LOGIN_URL as string;
  if (env?.trim()) return env.trim();
  if (typeof window !== 'undefined') return `${window.location.origin}/client/login`;
  return '/client/login';
}

/* ---------------------------------------------------------------- types */

interface Client {
  id: number;
  client_code: string;
  company_name: string;
  entity_type: string;
  registration_number: string | null;
  contact_email: string;
  status: string;
  kyb_status: string;
  onboarding_status: string | null;
  partner_type: string | null;
  passthrough_enabled?: boolean;
  mwallet_merchant_partner?: boolean;
  sanctions_pep_status: string | null;
  pep_declared?: boolean | null;
  kyb_reviewed_by: number | null;
  ledger_account_code: string | null;
  deposit_reference: string | null;
  white_label_slug: string | null;
  invite_sent_at?: string | null;
  risk_tier: string | null;
  minimum_float_cents: number | null;
  uatOnboardingRelaxedFourEyes?: boolean;
}

interface KybDoc {
  id: number;
  document_type: string;
  status: string;
  verification_score: number | null;
  validation_result: { errors?: string[]; warnings?: string[] } | null;
  created_at: string;
}

interface RequiredDoc {
  documentType: string;
  description: string;
  required: boolean;
}

interface AuditEntry {
  id: number;
  actor: string;
  actor_user_id: number | null;
  action: string;
  from_status: string | null;
  to_status: string | null;
  reason: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
}

/* ---------------------------------------------------------------- constants */

const ENTITY_TYPE_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'close_corporation', label: 'Close Corporation' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'trust', label: 'Trust' },
];

const PARTNER_TYPE_OPTIONS = [
  { value: 'disbursement', label: 'Disbursement client' },
  { value: 'channel_partner', label: 'Channel partner' },
  { value: 'hybrid', label: 'Hybrid (disbursement + channel partner)' },
  { value: 'client', label: 'Client' },
];

const RISK_TIERS = ['low', 'medium', 'high', 'critical'];

const DOC_TYPE_LABELS: Record<string, string> = {
  cor15: 'CIPC Registration',
  cor14_3: 'CoR14.3 (Directors)',
  trust_deed: 'Trust Deed',
  id_document: 'ID Document',
  proof_of_address: 'Proof of Address',
  bank_confirmation: 'Bank Confirmation',
  tax_clearance: 'Tax clearance (SARS TCS or VAT103)',
  resolution: 'Resolution / Letter of Authority',
};

const formatDocType = (dt: string) => DOC_TYPE_LABELS[dt] || dt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const DOC_OK_STATUSES = new Set(['validated', 'verified', 'approved']);
const DOC_CLOSED_STATUSES = new Set(['rejected', 'validation_failed', 'expired']);

function latestDocsByType(documents: KybDoc[]): Array<KybDoc & { earlierCount: number }> {
  const byType = new Map<string, KybDoc[]>();
  for (const doc of documents) {
    const list = byType.get(doc.document_type) || [];
    list.push(doc);
    byType.set(doc.document_type, list);
  }
  const out: Array<KybDoc & { earlierCount: number }> = [];
  for (const list of byType.values()) {
    const sorted = [...list].sort((a, b) => {
      const aOk = DOC_OK_STATUSES.has(a.status) ? 1 : 0;
      const bOk = DOC_OK_STATUSES.has(b.status) ? 1 : 0;
      if (bOk !== aOk) return bOk - aOk;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    out.push({ ...sorted[0], earlierCount: list.length - 1 });
  }
  return out.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

function hasCurrentUpload(documents: KybDoc[], documentType: string): boolean {
  return documents.some((d) => d.document_type === documentType && !DOC_CLOSED_STATUSES.has(d.status));
}

const DOC_STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  processing: 'bg-blue-50 text-blue-700',
  extracted: 'bg-blue-50 text-blue-700',
  validated: 'bg-teal-50 text-teal-700',
  validation_failed: 'bg-red-50 text-red-700',
  verified: 'bg-emerald-50 text-emerald-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
};

type StepId = 'create' | 'in_progress' | 'form' | 'documents' | 'screening' | 'verify' | 'review' | 'approve' | 'done';

const STEPS: { id: StepId; label: string }[] = [
  { id: 'create', label: 'Create' },
  { id: 'form', label: 'Onboarding form' },
  { id: 'documents', label: 'KYB documents' },
  { id: 'screening', label: 'Screening' },
  { id: 'verify', label: 'AI verification' },
  { id: 'review', label: 'Maker review' },
  { id: 'approve', label: 'Checker approve' },
  { id: 'done', label: 'Activated' },
];

/** Clients that still need admin onboarding / activation work. */
function isResumableOnboarding(c: Client): boolean {
  if (c.status === 'active' && c.onboarding_status === 'activated') return false;
  if (c.status === 'closed') return false;
  return Boolean(c.onboarding_status);
}

/** Pending onboarding rows that may be hard-deleted (never activated). */
function canDeletePendingOnboarding(c: Client): boolean {
  if (c.onboarding_status === 'activated' || c.status === 'active') return false;
  if (c.ledger_account_code) return false;
  return isResumableOnboarding(c);
}

/** Derive the furthest reached step from the server onboarding status. */
function stepFromStatus(c: Client | null): StepId {
  if (!c) return 'create';
  if (c.onboarding_status === 'activated' || c.status === 'active') return 'done';

  const kybReady = c.kyb_status === 'verified' || c.kyb_status === 'approved';
  const screeningDone = c.sanctions_pep_status === 'cleared' || c.sanctions_pep_status === 'flagged';

  if (c.onboarding_status === 'under_review' || (kybReady && c.status === 'pending')) {
    if (kybReady && c.kyb_reviewed_by) return 'approve';
    if (kybReady && screeningDone) return 'review';
    if (screeningDone) return 'review';
    return 'screening';
  }

  if (c.onboarding_status === 'submitted') return kybReady ? 'screening' : 'documents';
  if (c.onboarding_status === 'invited') return 'in_progress';
  if (c.onboarding_status === 'not_started' || !c.onboarding_status) return 'form';
  return 'form';
}

function activationStepLabel(c: Client): string {
  if (c.status === 'active') return 'Activated';
  if (c.kyb_status === 'verified' && c.kyb_reviewed_by) return 'Checker approve';
  if (c.kyb_status === 'verified') return 'Maker review';
  if (c.onboarding_status === 'under_review') return 'Screening / review';
  return c.onboarding_status || 'In progress';
}

function previewClientCode(companyName: string): string {
  const base = companyName.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
  return base.length >= 2 ? base : 'PARTNER';
}

function previewDepositRef(clientCode: string): string {
  const base = clientCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return `CP${base}`.slice(0, 40);
}

const INITIAL_CREATE_FORM = {
  company_name: '',
  entity_type: 'company',
  registration_number: '',
  contact_email: '',
  contact_name: '',
  contact_phone: '',
  partner_type: 'channel_partner',
  client_code: '',
  revenue_share_enabled: false,
  revenue_share_pct: '',
  passthrough_enabled: false,
  mwallet_merchant_partner: false,
  wallet_upgrade_permitted: false,
  cpa_classification: 'managed_portal',
  cpa_modes: ['client_portal'] as string[],
  cpa_services: ['wallet_disbursement'] as string[],
};

/* ---------------------------------------------------------------- component */

export const PartnerOnboardingOverlay: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);
  const [step, setStep] = useState<StepId>('create');
  const [docs, setDocs] = useState<KybDoc[]>([]);
  const [requiredDocs, setRequiredDocs] = useState<RequiredDoc[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [resumeList, setResumeList] = useState<Client[]>([]);
  const [hasReport, setHasReport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string>>({});
  const [showAdvancedOverride, setShowAdvancedOverride] = useState(false);
  const [createResult, setCreateResult] = useState<{
    emailSent: boolean;
    emailError?: string | null;
    depositReferencePreview?: string | null;
    portalLoginUrl?: string | null;
    emailSentAt: string;
  } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshReportAvailability = useCallback(async (id: number) => {
    try {
      const rep = await API.get(`/disbursement-clients/${id}/onboarding/report`);
      setHasReport(!!rep.data.data?.has_report);
    } catch {
      setHasReport(false);
    }
  }, []);

  const openReport = async () => {
    if (!client) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await API.get(`/disbursement-clients/${client.id}/onboarding/report/content`, {
        responseType: 'text',
      });
      const blob = new Blob([res.data], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      fail(err, 'Could not open onboarding report');
    } finally {
      setBusy(false);
    }
  };

  /* ---------- create form ---------- */
  const [createForm, setCreateForm] = useState({ ...INITIAL_CREATE_FORM });

  /* ---------- onboarding form (Sections A–E) ---------- */
  const [obForm, setObForm] = useState({
    legal_name: '',
    entity_type: 'company',
    reg_number: '',
    registered_address: '',
    signatory_name: '',
    signatory_id: '',
    account_number: '',
    expected_monthly_volume: '',
  });

  /* ---------- screening / approve inputs ---------- */
  const [screeningStatus, setScreeningStatus] = useState<'cleared' | 'flagged'>('cleared');
  const [pepDeclared, setPepDeclared] = useState<'yes' | 'no' | ''>('');
  const [screeningNotes, setScreeningNotes] = useState('');
  const [reviewReason, setReviewReason] = useState('');
  const [approveTier, setApproveTier] = useState('low');
  const [approveFloatRands, setApproveFloatRands] = useState('');
  const [checkerEmail, setCheckerEmail] = useState('');
  const [checkerPassword, setCheckerPassword] = useState('');
  const [checkerFieldErrors, setCheckerFieldErrors] = useState<Record<string, string>>({});

  const { user: makerUser } = useAuth();

  /* ---------- doc upload ---------- */
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ---------- helpers ---------- */

  const refreshClient = useCallback(async (id: number) => {
    const res = await API.get(`/disbursement-clients/${id}`);
    setClient(res.data.data as Client);
    return res.data.data as Client;
  }, []);

  const refreshDocs = useCallback(async (id: number) => {
    try {
      const res = await API.get(`/disbursement-clients/${id}/kyb-documents`);
      setDocs(res.data.data.documents || []);
      setRequiredDocs(res.data.data.requiredDocuments || []);
    } catch { /* non-fatal */ }
  }, []);

  const refreshAudit = useCallback(async (id: number) => {
    try {
      const res = await API.get(`/disbursement-clients/${id}/onboarding/audit-log`);
      setAudit(res.data.data.entries || []);
    } catch { /* non-fatal */ }
  }, []);

  const loadResumeList = useCallback(async () => {
    try {
      const res = await API.get('/disbursement-clients', { params: { limit: 100 } });
      const all: Client[] = res.data.data.clients || [];
      setResumeList(all.filter(isResumableOnboarding));
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadResumeList(); }, [loadResumeList]);

  // Poll KYB doc extraction status while on the documents step (async OCR).
  useEffect(() => {
    if (step === 'documents' && client) {
      pollRef.current = setInterval(() => { refreshDocs(client.id); }, 5000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [step, client, refreshDocs]);

  const loadClientIntoWizard = useCallback(async (id: number) => {
    setBusy(true);
    setMsg(null);
    try {
      const c = await refreshClient(id);
      await Promise.all([refreshDocs(id), refreshAudit(id)]);
      setObForm((p) => ({
        ...p,
        legal_name: c.company_name || '',
        entity_type: ENTITY_TYPE_OPTIONS.some((o) => o.value === c.entity_type) ? c.entity_type : 'company',
        reg_number: c.registration_number || '',
      }));
      setStep(stepFromStatus(c));
      await refreshReportAvailability(id);
    } catch (err: any) {
      setMsg({ type: 'error', text: err?.response?.data?.error || 'Failed to load onboarding' });
    } finally {
      setBusy(false);
    }
  }, [refreshClient, refreshDocs, refreshAudit, refreshReportAvailability]);

  useEffect(() => {
    const raw = searchParams.get('clientId');
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0) return;
    loadClientIntoWizard(id);
  }, [searchParams, loadClientIntoWizard]);

  const ok = (text: string) => setMsg({ type: 'success', text });
  const fail = (err: any, fallback: string) => setMsg({ type: 'error', text: err?.response?.data?.error || fallback });

  /* ---------- actions ---------- */

  const startNew = () => {
    setClient(null);
    setDocs([]);
    setRequiredDocs([]);
    setAudit([]);
    setHasReport(false);
    setCreateResult(null);
    setCreateForm({ ...INITIAL_CREATE_FORM });
    setCreateFieldErrors({});
    setShowAdvancedOverride(false);
    setStep('create');
    setMsg(null);
  };

  const handleDeletePending = async (c: Client) => {
    const label = `${c.client_code} — ${c.company_name}`;
    if (!window.confirm(
      `Permanently delete pending onboarding for ${label}?\n\nKYB documents, uploaded agreements, and audit history for this draft will be removed. Activated partners are not affected. This cannot be undone.`,
    )) return;

    setDeletingId(c.id);
    setMsg(null);
    try {
      await API.delete(`/disbursement-clients/${c.id}`);
      if (client?.id === c.id) startNew();
      await loadResumeList();
      ok(`Deleted ${c.client_code}.`);
    } catch (err: any) {
      fail(err, 'Failed to delete pending onboarding');
    } finally {
      setDeletingId(null);
    }
  };

  const doCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);

    const errors = validatePartnerOnboardingCreateForm(createForm);
    if (Object.keys(errors).length > 0) {
      setCreateFieldErrors(errors);
      setMsg({ type: 'error', text: 'Please fix the highlighted fields before continuing.' });
      return;
    }
    setCreateFieldErrors({});

    setBusy(true);
    try {
      const payload: Record<string, any> = {
        company_name: createForm.company_name.trim(),
        entity_type: createForm.entity_type,
        registration_number: createForm.registration_number.trim() || null,
        contact_email: createForm.contact_email.trim(),
        partner_type: createForm.partner_type,
        passthrough_enabled: createForm.passthrough_enabled,
        mwallet_merchant_partner: createForm.mwallet_merchant_partner,
        revenue_share_enabled: createForm.revenue_share_enabled,
        wallet_upgrade_permitted: createForm.wallet_upgrade_permitted,
      };
      if (createForm.contact_name.trim()) payload.contact_name = createForm.contact_name.trim();
      if (createForm.contact_phone.trim()) payload.contact_phone = createForm.contact_phone.trim();
      if (showAdvancedOverride && createForm.client_code.trim()) {
        payload.client_code = createForm.client_code.trim();
      }
      if (createForm.revenue_share_enabled && createForm.revenue_share_pct.trim()) {
        payload.revenue_share_pct = parseFloat(createForm.revenue_share_pct);
      }
      payload.cpa_selections = {
        classification: createForm.cpa_classification,
        integration_modes: createForm.cpa_modes,
        platform_services: createForm.cpa_services,
      };

      const res = await API.post('/disbursement-clients', payload);
      const data = res.data.data;
      const created = data.client as Client;
      setClient(created);
      setCreateResult({
        emailSent: !!data.emailSent,
        emailError: data.emailError || null,
        depositReferencePreview: data.depositReferencePreview || previewDepositRef(created.client_code),
        portalLoginUrl: data.portalLoginUrl || clientPortalLoginUrl(),
        emailSentAt: new Date().toISOString(),
      });
      await refreshAudit(created.id);
      setStep('in_progress');
      ok(`Partner ${created.company_name} created. Welcome email ${data.emailSent ? 'sent' : 'could not be sent (check SMTP)'}.`);
      loadResumeList();
    } catch (err: any) {
      fail(err, 'Failed to create client');
    } finally {
      setBusy(false);
    }
  };

  const doSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    setBusy(true);
    setMsg(null);
    try {
      const form: Record<string, any> = {
        legal_name: obForm.legal_name.trim(),
        entity_type: obForm.entity_type,
        reg_number: obForm.reg_number.trim(),
        registered_address: obForm.registered_address.trim(),
        signatory_name: obForm.signatory_name.trim(),
        signatory_id: obForm.signatory_id.trim(),
      };
      if (obForm.account_number.trim()) form.account_number = obForm.account_number.trim();
      if (obForm.expected_monthly_volume.trim()) form.expected_monthly_volume = Number(obForm.expected_monthly_volume);

      await API.post(`/disbursement-clients/${client.id}/onboarding/submit`, { form });
      await refreshClient(client.id);
      await refreshAudit(client.id);
      await refreshDocs(client.id);
      setStep('documents');
      ok('Onboarding form submitted. PII fields are encrypted at rest.');
    } catch (err: any) {
      fail(err, 'Failed to submit onboarding form');
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async () => {
    if (!client || !uploadDocType || !uploadFile) return;
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('document', uploadFile);
      fd.append('document_type', uploadDocType);
      await API.post(`/disbursement-clients/${client.id}/kyb-documents`, fd);
      setUploadDocType('');
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refreshDocs(client.id);
      await refreshAudit(client.id);
      ok('Document uploaded — AI extraction & validation running in the background.');
    } catch (err: any) {
      fail(err, 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const viewDoc = async (docId: number) => {
    if (!client) return;
    try {
      const res = await API.get(`/disbursement-clients/${client.id}/kyb-documents/${docId}/review-url`);
      const url = res?.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else fail(new Error('No URL returned'), 'Could not open document');
    } catch (err: any) {
      fail(err, 'Could not open document');
    }
  };

  const doScreening = async () => {
    if (!client) return;
    if (!pepDeclared) {
      fail(new Error('Answer the PEP declaration first'), 'PEP declaration required');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await API.post(`/disbursement-clients/${client.id}/onboarding/screening`, {
        status: screeningStatus,
        notes: screeningNotes.trim() || undefined,
        pepDeclared: pepDeclared === 'yes',
      });
      await refreshClient(client.id);
      await refreshAudit(client.id);
      ok(`Sanctions screening recorded: ${screeningStatus}.`);
      if (screeningStatus === 'cleared') setStep('verify');
    } catch (err: any) {
      fail(err, 'Failed to record screening');
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async () => {
    if (!client) return;
    setBusy(true);
    setMsg(null);
    try {
      await API.post(`/disbursement-clients/${client.id}/onboarding/verify`, {});
      const c = await refreshClient(client.id);
      await refreshAudit(client.id);
      await refreshReportAvailability(client.id);
      ok('AI verification complete. Onboarding moved to under review.');
      setStep('review');
      void c;
    } catch (err: any) {
      fail(err, 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const doMakerReview = async (decision: 'verify' | 'reject') => {
    if (!client) return;
    setBusy(true);
    setMsg(null);
    try {
      await API.post(`/disbursement-clients/${client.id}/onboarding/review`, {
        decision,
        reason: reviewReason.trim() || undefined,
      });
      const c = await refreshClient(client.id);
      await refreshAudit(client.id);
      if (decision === 'verify') {
        ok('Maker verified. A different user must now approve (four-eyes).');
        setStep('approve');
      } else {
        setMsg({ type: 'info', text: 'Onboarding rejected by maker.' });
        setStep(stepFromStatus(c));
      }
    } catch (err: any) {
      fail(err, 'Maker review failed');
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async () => {
    if (!client) return;
    setMsg(null);

    const body: Record<string, any> = { risk_tier: approveTier };
    if (approveFloatRands.trim()) {
      body.minimum_float_cents = Math.round(parseFloat(approveFloatRands) * 100);
    }

    const uatRelaxed = !!client.uatOnboardingRelaxedFourEyes;

    if (!uatRelaxed) {
      const errors: Record<string, string> = {};
      const emailErr = validateEmail(checkerEmail);
      if (emailErr) errors.checker_email = emailErr;
      if (!checkerPassword.trim()) errors.checker_password = 'Checker password is required';
      const normalizedChecker = checkerEmail.trim().toLowerCase();
      const makerEmail = (makerUser?.email || '').toLowerCase();
      if (normalizedChecker && makerEmail && normalizedChecker === makerEmail) {
        errors.checker_email = 'Checker must be a different portal user than the maker';
      }
      if (Object.keys(errors).length > 0) {
        setCheckerFieldErrors(errors);
        setMsg({ type: 'error', text: 'Enter checker credentials for a different portal user (four-eyes).' });
        return;
      }
      setCheckerFieldErrors({});

      setBusy(true);
      try {
        const loginRes = await axios.post('/api/v1/admin/auth/login', {
          email: normalizedChecker,
          password: checkerPassword,
        });
        const checkerToken = loginRes.data?.data?.token;
        if (!checkerToken) {
          throw new Error(loginRes.data?.error || 'Checker login failed');
        }

        await axios.post(`/api/v1/disbursement-clients/${client.id}/onboarding/approve`, body, {
          headers: { Authorization: `Bearer ${checkerToken}` },
        });

        setCheckerPassword('');
        await refreshClient(client.id);
        await refreshAudit(client.id);
        ok('Client activated. Partner float provisioned.');
        setStep('done');
        loadResumeList();
      } catch (err: any) {
        fail(err, 'Approval failed');
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      await API.post(`/disbursement-clients/${client.id}/onboarding/approve`, body);
      await refreshClient(client.id);
      await refreshAudit(client.id);
      ok('Client activated (UAT — four-eyes bypass). Partner float provisioned.');
      setStep('done');
      loadResumeList();
    } catch (err: any) {
      fail(err, 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  /* ---------- derived ---------- */

  const serverStep = stepFromStatus(client);
  const stepperId = (s: StepId): StepId => (s === 'in_progress' ? 'create' : s);
  const currentIndex = STEPS.findIndex((s) => s.id === stepperId(step));
  const serverIndex = STEPS.findIndex((s) => s.id === stepperId(serverStep));

  const canGoTo = (id: StepId) => {
    const idx = STEPS.findIndex((s) => s.id === id);
    if (!client) return id === 'create';
    return idx <= Math.max(serverIndex, currentIndex);
  };

  const repLink = client?.white_label_slug ? `${WALLET_BASE_URL}/register/${client.white_label_slug}` : null;

  /* ---------------------------------------------------------------- render */

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">Partner Onboarding</h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">
            Banking-grade KYB onboarding: create → form → documents → screening → verification → maker → checker (four-eyes) → activation.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <select
            onChange={(e) => { if (e.target.value) loadClientIntoWizard(Number(e.target.value)); }}
            value={client && isResumableOnboarding(client) ? client.id : ''}
            className="min-w-[14rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            aria-label="Resume partner onboarding"
          >
            <option value="">
              {resumeList.length ? 'Resume in-progress…' : 'No in-progress onboardings'}
            </option>
            {resumeList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.client_code} — {c.company_name} ({activationStepLabel(c)})
              </option>
            ))}
          </select>
          <button
            onClick={startNew}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]"
          >
            + New Onboarding
          </button>
        </div>
      </div>

      {resumeList.length > 0 && !client && (
        <div className="rounded-xl border border-[var(--border)] bg-white p-4 space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--foreground)]">Continue partner activation</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-1">
              KYB document verification in Disbursement Clients does not activate the partner. Select a client below to run screening, maker review, and checker approval.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400 border-b">
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">KYB</th>
                  <th className="py-2 pr-3">Next step</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {resumeList.map((c) => (
                  <tr key={c.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 pr-3 font-medium">{c.client_code} — {c.company_name}</td>
                    <td className="py-2 pr-3 capitalize">{(c.kyb_status || '').replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-3">{activationStepLabel(c)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => loadClientIntoWizard(c.id)}
                        className="text-[var(--primary)] font-semibold hover:underline"
                      >
                        Open →
                      </button>
                      {canDeletePendingOnboarding(c) && (
                        <>
                          <span className="text-gray-300 mx-2">|</span>
                          <button
                            type="button"
                            onClick={() => handleDeletePending(c)}
                            disabled={deletingId === c.id}
                            className="text-red-600 font-semibold hover:underline disabled:opacity-50"
                          >
                            {deletingId === c.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {msg && (
        <div className={`rounded-xl p-4 text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700'
            : msg.type === 'error' ? 'bg-red-50 text-red-700'
            : 'bg-blue-50 text-blue-700'
        }`}>
          {msg.text}
        </div>
      )}

      {/* Stepper */}
      <div className="bg-white rounded-xl border border-[var(--border)] p-4 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {STEPS.map((s, i) => {
            const reached = i < serverIndex || i < currentIndex
              || (step === 'in_progress' && s.id === 'create')
              || (client && s.id === 'done' && serverStep === 'done');
            const active = s.id === step || (step === 'in_progress' && s.id === 'create' && false);
            const clickable = canGoTo(s.id);
            return (
              <React.Fragment key={s.id}>
                <button
                  disabled={!clickable}
                  onClick={() => clickable && setStep(s.id)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                      : reached ? 'bg-emerald-50 text-emerald-700'
                      : clickable ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/30 text-[10px]">{i + 1}</span>
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <span className="text-gray-300">›</span>}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {client && (
        <div className="bg-white rounded-xl border border-[var(--border)] p-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
          <span><span className="text-gray-400">Client:</span> <strong>{client.company_name}</strong> ({client.client_code})</span>
          <span><span className="text-gray-400">Onboarding:</span> {client.onboarding_status || 'not_started'}</span>
          <span><span className="text-gray-400">KYB:</span> {client.kyb_status}</span>
          <span><span className="text-gray-400">Screening:</span> {client.sanctions_pep_status || 'pending'}</span>
          {canDeletePendingOnboarding(client) && (
            <button
              type="button"
              onClick={() => handleDeletePending(client)}
              disabled={deletingId === client.id}
              className="ml-auto text-red-600 text-xs font-semibold hover:underline disabled:opacity-50"
            >
              {deletingId === client.id ? 'Deleting…' : 'Delete pending onboarding'}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active step panel */}
        <div className="lg:col-span-2 space-y-6">
          {step === 'create' && renderCreate()}
          {step === 'in_progress' && renderInProgress()}
          {step === 'form' && renderForm()}
          {step === 'documents' && renderDocuments()}
          {step === 'screening' && renderScreening()}
          {step === 'verify' && renderVerify()}
          {step === 'review' && renderReview()}
          {step === 'approve' && renderApprove()}
          {step === 'done' && renderDone()}
        </div>

        {/* Audit trail */}
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-[var(--border)] p-4">
            <h3 className="text-sm font-semibold text-[var(--foreground)] mb-3">Audit trail</h3>
            {audit.length === 0 ? (
              <p className="text-xs text-gray-400">No audit entries yet.</p>
            ) : (
              <ol className="space-y-3">
                {audit.map((a) => (
                  <li key={a.id} className="border-l-2 border-gray-200 pl-3">
                    <p className="text-xs font-semibold text-gray-700">{a.action.replace(/_/g, ' ')}</p>
                    <p className="text-[11px] text-gray-400">
                      {new Date(a.created_at).toLocaleString('en-ZA')} · {a.actor}
                      {a.actor_user_id ? ` #${a.actor_user_id}` : ''}
                    </p>
                    {(a.from_status || a.to_status) && (
                      <p className="text-[11px] text-gray-500">{a.from_status || '—'} → {a.to_status || '—'}</p>
                    )}
                    {a.reason && <p className="text-[11px] text-gray-500 italic">{a.reason}</p>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- step renderers */

  function obInput(
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts: { type?: string; placeholder?: string; required?: boolean } = {},
  ) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}{opts.required ? ' *' : ''}</label>
        <input
          type={opts.type || 'text'}
          value={value}
          required={opts.required}
          placeholder={opts.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
        />
      </div>
    );
  }

  function input(
    label: string,
    field: keyof typeof createForm,
    value: string,
    onChange: (v: string) => void,
    opts: { type?: string; placeholder?: string; required?: boolean; inputMode?: string } = {},
  ) {
    const hasError = !!createFieldErrors[field];
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}{opts.required ? ' *' : ''}</label>
        <input
          type={opts.type || 'text'}
          inputMode={opts.inputMode as React.HTMLAttributes<HTMLInputElement>['inputMode']}
          value={value}
          required={opts.required}
          placeholder={opts.placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            if (createFieldErrors[field]) {
              setCreateFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
              });
            }
          }}
          onBlur={() => {
            const errors = validatePartnerOnboardingCreateForm({ ...createForm, [field]: value });
            setCreateFieldErrors((prev) => {
              const next = { ...prev };
              if (errors[field]) next[field] = errors[field];
              else delete next[field];
              return next;
            });
          }}
          className={fieldInputClass(hasError)}
        />
        {hasError && <p className="mt-1 text-xs text-red-600">{createFieldErrors[field]}</p>}
      </div>
    );
  }

  function renderCreate() {
    const codePreview = createForm.client_code.trim() || previewClientCode(createForm.company_name);
    const depositPreview = codePreview ? previewDepositRef(codePreview) : '—';
    const portalUrl = clientPortalLoginUrl();

    return (
      <form onSubmit={doCreate} noValidate className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">1. Create partner / client</h2>
        <p className="text-xs text-gray-500">
          MyMoolah generates a unique client code and deposit reference. The partner receives Client Portal login
          credentials by email to complete onboarding.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {input('Company Name', 'company_name', createForm.company_name, (v) => {
            setCreateForm((p) => ({ ...p, company_name: v }));
          }, { required: true })}
          {input('Registration Number', 'registration_number', createForm.registration_number, (v) => {
            setCreateForm((p) => ({ ...p, registration_number: v }));
          }, { placeholder: 'e.g. 2015/123456/07', inputMode: 'text' })}
          <div className="sm:col-span-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">System-generated identifiers (preview)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700">
              <p><span className="text-gray-400">Client code:</span> <strong className="font-mono">{codePreview || '—'}</strong></p>
              <p><span className="text-gray-400">Deposit ref:</span> <strong className="font-mono">{depositPreview}</strong></p>
            </div>
          </div>
          <p className="sm:col-span-2 -mt-2 text-xs text-gray-400">
            The registration number pre-fills the NDA and CPA so the partner can review and accept them without
            editing the document text. If left blank, the partner will be asked to complete company details first.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Entity Type *</label>
            <select value={createForm.entity_type} onChange={(e) => setCreateForm((p) => ({ ...p, entity_type: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200">
              {ENTITY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Partner Type</label>
            <select value={createForm.partner_type} onChange={(e) => setCreateForm((p) => ({ ...p, partner_type: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200">
              {PARTNER_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {input('Contact Email', 'contact_email', createForm.contact_email, (v) => setCreateForm((p) => ({ ...p, contact_email: v })), { required: true, inputMode: 'email' })}
          {input('Contact Name', 'contact_name', createForm.contact_name, (v) => setCreateForm((p) => ({ ...p, contact_name: v })))}
          {input('Contact Phone', 'contact_phone', createForm.contact_phone, (v) => setCreateForm((p) => ({ ...p, contact_phone: v })), { inputMode: 'tel' })}
          <div className="sm:col-span-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium text-gray-600 mb-1">Client Portal URL</p>
            <p className="text-sm font-mono text-gray-800 break-all">{portalUrl}</p>
            <p className="mt-1 text-xs text-gray-400">
              The partner receives this link and temporary login credentials by email to complete onboarding (steps 2–7).
            </p>
          </div>
        </div>
        <div className="pt-2 border-t border-gray-100">
          <button type="button" onClick={() => setShowAdvancedOverride((v) => !v)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700">
            {showAdvancedOverride ? 'Hide' : 'Show'} advanced override (client code)
          </button>
          {showAdvancedOverride && (
            <div className="mt-2">
              {input('Client Code override', 'client_code', createForm.client_code, (v) => setCreateForm((p) => ({ ...p, client_code: v })), {
                placeholder: 'Only if contract requires a specific code',
              })}
            </div>
          )}
        </div>
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={createForm.revenue_share_enabled} onChange={(e) => setCreateForm((p) => ({ ...p, revenue_share_enabled: e.target.checked }))} className="rounded border-gray-300" />
            Revenue Share Partner (Revenue Share Agreement presented for e-signature)
          </label>
          {createForm.revenue_share_enabled && (
            <div className="pl-6">
              {input('Revenue share %', 'revenue_share_pct', createForm.revenue_share_pct, (v) => setCreateForm((p) => ({ ...p, revenue_share_pct: v })), { type: 'number', placeholder: 'e.g. 20' })}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={createForm.passthrough_enabled} onChange={(e) => {
              const passthrough = e.target.checked;
              setCreateForm((p) => {
                if (passthrough) {
                  const isHybrid = p.partner_type === 'hybrid';
                  return {
                    ...p,
                    passthrough_enabled: true,
                    cpa_classification: isHybrid ? 'hybrid' : 'direct_api',
                    cpa_modes: isHybrid ? ['client_portal', 'partner_api'] : ['partner_api'],
                    cpa_services: isHybrid ? ['wallet_disbursement', 'vas_passthrough'] : ['vas_passthrough'],
                  };
                }
                return {
                  ...p,
                  passthrough_enabled: false,
                  cpa_classification: 'managed_portal',
                  cpa_modes: ['client_portal'],
                  cpa_services: ['wallet_disbursement'],
                };
              });
            }} className="rounded border-gray-300" />
            Pass-through (closed-loop VAS) enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={createForm.mwallet_merchant_partner} onChange={(e) => setCreateForm((p) => ({ ...p, mwallet_merchant_partner: e.target.checked }))} className="rounded border-gray-300" />
            M-Wallet merchant partner (replenishment model — Annex C)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={createForm.wallet_upgrade_permitted} onChange={(e) => setCreateForm((p) => ({ ...p, wallet_upgrade_permitted: e.target.checked }))} className="rounded border-gray-300" />
            Wallet upgrade (graduation) permitted
          </label>
        </div>
        <div className="space-y-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
          <p className="text-xs font-semibold text-gray-500 uppercase">Agreement setup</p>
          <p className="text-xs text-gray-500">The partner does not choose these. They are written into the Channel Partner Agreement. The partner only accepts.</p>
          <fieldset className="space-y-1">
            <legend className="text-xs font-medium text-gray-600">Partner classification</legend>
            {[
              ['managed_portal', 'Managed Portal and Disbursement Partner'],
              ['direct_api', 'Direct API and Technical Integrator'],
              ['hybrid', 'Hybrid Partner'],
            ].map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" name="cpa_classification" checked={createForm.cpa_classification === value}
                  onChange={() => setCreateForm((p) => ({ ...p, cpa_classification: value }))} className="border-gray-300" />
                {label}
              </label>
            ))}
          </fieldset>
          <fieldset className="space-y-1">
            <legend className="text-xs font-medium text-gray-600">Integration modes</legend>
            {[
              ['client_portal', 'Client Portal / Web GUI'],
              ['csv_batch', 'Self-help CSV batch uploads'],
              ['partner_api', 'Partner API'],
              ['sftp', 'SFTP batch processing'],
            ].map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={createForm.cpa_modes.includes(value)}
                  onChange={() => setCreateForm((p) => ({
                    ...p,
                    cpa_modes: p.cpa_modes.includes(value) ? p.cpa_modes.filter((item) => item !== value) : [...p.cpa_modes, value],
                  }))} className="rounded border-gray-300" />
                {label}
              </label>
            ))}
          </fieldset>
          <fieldset className="space-y-1">
            <legend className="text-xs font-medium text-gray-600">Platform services</legend>
            {[
              ['wallet_disbursement', 'Managed wallet and portal disbursements'],
              ['vas_passthrough', 'VAS pass-through distribution'],
              ['outbound_rails', 'Regulated outbound payment rails'],
            ].map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={createForm.cpa_services.includes(value)}
                  onChange={() => setCreateForm((p) => ({
                    ...p,
                    cpa_services: p.cpa_services.includes(value) ? p.cpa_services.filter((item) => item !== value) : [...p.cpa_services, value],
                  }))} className="rounded border-gray-300" />
                {label}
              </label>
            ))}
          </fieldset>
          {createFieldErrors.cpa_selections && <p className="text-xs text-red-600">{createFieldErrors.cpa_selections}</p>}
        </div>
        <button type="submit" disabled={busy}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
          {busy ? 'Creating…' : 'Create & continue'}
        </button>
      </form>
    );
  }

  function renderInProgress() {
    if (!client) return null;
    const sentAt = createResult?.emailSentAt
      ? new Date(createResult.emailSentAt).toLocaleString('en-ZA')
      : client.invite_sent_at
        ? new Date(client.invite_sent_at).toLocaleString('en-ZA')
        : '—';

    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-[var(--foreground)]">
            Onboarding for {client.company_name} in progress
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            The partner has been registered. They must sign in to the Client Portal and complete onboarding.
          </p>
        </div>

        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4 space-y-2 text-sm">
          <p><span className="text-gray-500">Client code:</span> <strong className="font-mono">{client.client_code}</strong></p>
          {createResult?.depositReferencePreview && (
            <p><span className="text-gray-500">Deposit reference (preview):</span> <strong className="font-mono">{createResult.depositReferencePreview}</strong></p>
          )}
          {repLink && (
            <p>
              <span className="text-gray-500">Rep registration link:</span>{' '}
              <a
                href={repLink}
                className="text-emerald-700 underline font-mono text-xs break-all"
                target="_blank"
                rel="noreferrer"
              >
                {repLink}
              </a>
            </p>
          )}
          <p>
            <span className="text-gray-500">Client Portal URL:</span>{' '}
            <a
              href={createResult?.portalLoginUrl || clientPortalLoginUrl()}
              className="text-emerald-700 underline font-mono text-xs break-all"
              target="_blank"
              rel="noreferrer"
            >
              {createResult?.portalLoginUrl || clientPortalLoginUrl()}
            </a>
          </p>
          <p>
            <span className="text-gray-500">Welcome email:</span>{' '}
            {createResult?.emailSent ? (
              <span className="text-emerald-700">Sent to <strong>{client.contact_email}</strong> at {sentAt}</span>
            ) : (
              <span className="text-amber-700">
                Not sent{createResult?.emailError ? ` (${createResult.emailError})` : ''} — check SMTP configuration
              </span>
            )}
          </p>
        </div>

        <div className="rounded-lg border border-amber-100 bg-amber-50/40 px-4 py-3 text-sm text-amber-900">
          <strong>Awaiting partner</strong> — the contact must log in, set their password, and complete the onboarding form and KYB uploads.
        </div>

        <div className="flex flex-wrap gap-3">
          <button type="button" disabled
            className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-400 cursor-not-allowed"
            title="Available once the partner submits their onboarding form">
            Continue to onboarding form
          </button>
          <a href={`/admin/channel-partners/${client.id}`}
            className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
            View client record
          </a>
          <button type="button" onClick={startNew}
            className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
            Start new partner
          </button>
        </div>
      </div>
    );
  }

  function renderForm() {
    return (
      <form onSubmit={doSubmitForm} className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">2. Onboarding form (Sections A–E)</h2>
        <p className="text-xs text-gray-400">Signatory ID and account number are encrypted at rest (AES-256-GCM) and masked in the audit record.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {obInput('Legal name', obForm.legal_name, (v) => setObForm((p) => ({ ...p, legal_name: v })), { required: true })}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Entity type *</label>
            <select value={obForm.entity_type} onChange={(e) => setObForm((p) => ({ ...p, entity_type: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200">
              {ENTITY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {obInput('Registration number', obForm.reg_number, (v) => setObForm((p) => ({ ...p, reg_number: v })), { required: true })}
          {obInput('Expected monthly volume (ZAR)', obForm.expected_monthly_volume, (v) => setObForm((p) => ({ ...p, expected_monthly_volume: v })), { type: 'number' })}
          {obInput('Authorised signatory name', obForm.signatory_name, (v) => setObForm((p) => ({ ...p, signatory_name: v })), { required: true })}
          {obInput('Signatory ID (encrypted)', obForm.signatory_id, (v) => setObForm((p) => ({ ...p, signatory_id: v })), { required: true, type: 'password' })}
          {obInput('Bank account number (encrypted)', obForm.account_number, (v) => setObForm((p) => ({ ...p, account_number: v })), { type: 'password' })}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Registered address *</label>
          <textarea value={obForm.registered_address} required rows={2}
            onChange={(e) => setObForm((p) => ({ ...p, registered_address: e.target.value }))}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200" />
        </div>
        <button type="submit" disabled={busy}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
          {busy ? 'Submitting…' : 'Submit form & continue'}
        </button>
      </form>
    );
  }

  function renderDocuments() {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">3. KYB documents</h2>
        <p className="text-xs text-gray-400">FICA minimum documents for your entity type. Upload clear JPG, PNG, or text-based PDF.</p>

        {requiredDocs.length > 0 && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Checklist</p>
            <ul className="space-y-1 text-sm text-gray-700">
              {requiredDocs.map((rd) => {
                const uploaded = docs.find((d) => d.document_type === rd.documentType);
                const ok = uploaded && ['validated', 'verified', 'approved'].includes(uploaded.status);
                return (
                  <li key={rd.documentType} className="flex items-start gap-2">
                    <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${ok ? 'bg-emerald-500' : uploaded ? 'bg-amber-400' : 'bg-gray-300'}`} />
                    <span>
                      {rd.description || formatDocType(rd.documentType)}
                      {!rd.required && <span className="text-gray-400"> (optional)</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-end bg-gray-50 rounded-lg p-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Document type</label>
            <select value={uploadDocType} onChange={(e) => setUploadDocType(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option>
              {requiredDocs
                .filter((rd) => !hasCurrentUpload(docs, rd.documentType))
                .map((rd) => (
                <option key={rd.documentType} value={rd.documentType}>
                  {rd.description || formatDocType(rd.documentType)}{rd.required ? '' : ' (optional)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">File (PDF / JPG / PNG)</label>
            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => setUploadFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
              className="block text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--primary)] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[var(--primary-foreground)]" />
          </div>
          <button onClick={doUpload} disabled={busy || !uploadDocType || !uploadFile}
            className="px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                {['Type', 'Status', 'Score', 'Note', ''].map((h) => (
                  <th key={h} className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {docs.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No documents uploaded yet</td></tr>
              ) : latestDocsByType(docs).map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {formatDocType(d.document_type)}
                    {d.earlierCount > 0 && (
                      <p className="text-[11px] text-gray-400 font-normal">{d.earlierCount} earlier upload hidden</p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${DOC_STATUS_BADGE[d.status] || 'bg-gray-100 text-gray-600'}`}>
                      {d.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{d.verification_score != null ? d.verification_score : '—'}</td>
                  <td className="px-3 py-2 text-xs text-red-600">{d.validation_result?.errors?.[0] || ''}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => viewDoc(d.id)} type="button"
                      className="text-xs font-semibold text-[var(--primary)] hover:underline">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <button onClick={() => setStep('screening')}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]">
            Continue to screening →
          </button>
        </div>
      </div>
    );
  }

  function renderScreening() {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">4. Sanctions screening</h2>
        <p className="text-xs text-gray-400">Record the compliance screening outcome. Activation requires a "cleared" result. FIC TFS hits email support and open an alert; they do not auto-fail this gate.</p>
        <div>
          <p className="text-sm font-medium text-[var(--foreground)] mb-1">
            Is any controlling person a politically exposed person, or a close family member or associate of one?
          </p>
          <p className="text-xs text-gray-400 mb-2">
            This is a declaration, not a PEP database. A yes opens enhanced due diligence. It does not block activation by itself.
          </p>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="pepDeclared" checked={pepDeclared === 'yes'} onChange={() => setPepDeclared('yes')} />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="pepDeclared" checked={pepDeclared === 'no'} onChange={() => setPepDeclared('no')} />
              No
            </label>
          </div>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="screening" checked={screeningStatus === 'cleared'} onChange={() => setScreeningStatus('cleared')} />
            Cleared
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="screening" checked={screeningStatus === 'flagged'} onChange={() => setScreeningStatus('flagged')} />
            Flagged
          </label>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
          <textarea value={screeningNotes} rows={2} onChange={(e) => setScreeningNotes(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200" />
        </div>
        <button onClick={doScreening} disabled={busy || !pepDeclared}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
          {busy ? 'Recording…' : 'Record screening'}
        </button>
      </div>
    );
  }

  function renderVerify() {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">5. AI verification & report</h2>
        <p className="text-xs text-gray-400">Aggregates document validation against the required matrix and generates the onboarding report. Moves the client to "under review".</p>
        <button onClick={doVerify} disabled={busy}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
          {busy ? 'Running…' : 'Run verification'}
        </button>
        {hasReport && (
          <p className="text-sm">
            <button type="button" onClick={openReport} disabled={busy}
              className="text-blue-600 hover:underline disabled:opacity-50">
              View onboarding report
            </button>
          </p>
        )}
      </div>
    );
  }

  function renderReview() {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">6. Maker review</h2>
        <p className="text-xs text-gray-400">The maker verifies the onboarding evidence. A different user must approve at the next step (four-eyes).</p>
        {hasReport && (
          <p className="text-sm">
            <button type="button" onClick={openReport} disabled={busy}
              className="text-blue-600 hover:underline disabled:opacity-50">
              View onboarding report
            </button>
          </p>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Reason / notes</label>
          <textarea value={reviewReason} rows={2} onChange={(e) => setReviewReason(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200" />
        </div>
        <div className="flex gap-3">
          <button onClick={() => doMakerReview('verify')} disabled={busy}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--primary)] text-[var(--primary-foreground)]">
            {busy ? 'Saving…' : 'Maker verify'}
          </button>
          <button onClick={() => doMakerReview('reject')} disabled={busy}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-red-300 text-red-600 hover:bg-red-50">
            Reject
          </button>
        </div>
      </div>
    );
  }

  function renderApprove() {
    const uatRelaxed = !!client?.uatOnboardingRelaxedFourEyes;
    const deploymentEnv = (client as any)?.deploymentEnv || (import.meta as any).env?.MODE || '';
    const isProdSingleApprover = uatRelaxed && deploymentEnv !== 'uat';
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[var(--foreground)]">7. Checker approval (four-eyes)</h2>
        {uatRelaxed ? (
          <div className="rounded-lg bg-sky-50 text-sky-900 text-xs p-3 space-y-1">
            <p>
              <strong>{isProdSingleApprover ? 'Single-approver mode' : 'UAT mode'}:</strong> four-eyes is relaxed.
              You can activate with your current admin session ({makerUser?.email || 'signed in'}).
              {isProdSingleApprover
                ? ' Switch CP_ONBOARDING_SINGLE_APPROVER=false to require a different checker user.'
                : ' Production requires a different checker user.'}
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-amber-50 text-amber-800 text-xs p-3 space-y-1">
            <p>
              The <strong>checker</strong> must be a <strong>different</strong> MMTP portal user than the maker.
              Approval provisions the partner float (2100-20-NNN) and the SBSA deposit reference.
            </p>
            {makerUser?.email && (
              <p>Current session (maker): <strong>{makerUser.email}</strong></p>
            )}
            <p className="text-amber-900/80">
              Production checker: <strong>{PRODUCTION_CHECKER_EMAIL}</strong> (must differ from the maker).
              UAT strict mode: set <code className="text-[11px] bg-amber-100/80 px-1 rounded">CP_ONBOARDING_STRICT_FOUR_EYES=true</code>.
            </p>
          </div>
        )}

        {!uatRelaxed && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Checker sign-in</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Checker email *</label>
              <input
                type="text"
                inputMode="email"
                autoComplete="username"
                value={checkerEmail}
                onChange={(e) => {
                  setCheckerEmail(e.target.value);
                  if (checkerFieldErrors.checker_email) {
                    setCheckerFieldErrors((p) => { const n = { ...p }; delete n.checker_email; return n; });
                  }
                }}
                placeholder={PRODUCTION_CHECKER_EMAIL}
                className={fieldInputClass(!!checkerFieldErrors.checker_email)}
              />
              {checkerFieldErrors.checker_email && (
                <p className="mt-1 text-xs text-red-600">{checkerFieldErrors.checker_email}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Checker password *</label>
              <input
                type="password"
                autoComplete="current-password"
                value={checkerPassword}
                onChange={(e) => {
                  setCheckerPassword(e.target.value);
                  if (checkerFieldErrors.checker_password) {
                    setCheckerFieldErrors((p) => { const n = { ...p }; delete n.checker_password; return n; });
                  }
                }}
                className={fieldInputClass(!!checkerFieldErrors.checker_password)}
              />
              {checkerFieldErrors.checker_password && (
                <p className="mt-1 text-xs text-red-600">{checkerFieldErrors.checker_password}</p>
              )}
            </div>
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Risk tier</label>
            <select value={approveTier} onChange={(e) => setApproveTier(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              {RISK_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Minimum float (ZAR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={approveFloatRands}
              onChange={(e) => setApproveFloatRands(e.target.value)}
              placeholder="e.g. 10000"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
            />
          </div>
        </div>
        <button onClick={doApprove} disabled={busy}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 bg-[var(--success-color)] text-white">
          {busy ? 'Approving…' : uatRelaxed ? (isProdSingleApprover ? 'Activate partner' : 'Activate partner (UAT)') : 'Sign in checker & activate'}
        </button>
      </div>
    );
  }

  function renderDone() {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6 space-y-4">
        <h2 className="text-lg font-semibold text-emerald-700">Activated</h2>
        <p className="text-sm text-gray-600">The partner is live. Share the funding reference and rep link below.</p>
        <div className="grid grid-cols-1 gap-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase w-44 shrink-0">Partner float account</span>
            <span className="font-mono text-gray-800">{client?.ledger_account_code || '—'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase w-44 shrink-0">SBSA deposit reference</span>
            <span className="font-mono text-gray-800">{client?.deposit_reference || '—'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase w-44 shrink-0">Risk tier</span>
            <span className="text-gray-800">{client?.risk_tier || '—'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase w-44 shrink-0">Rep registration link</span>
            {repLink ? (
              <a href={repLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{repLink}</a>
            ) : (
              <span className="text-gray-400">No white-label slug set</span>
            )}
          </div>
        </div>
      </div>
    );
  }
};
